/**
 * `/signup` — learner self-signup (SCH-1, ADR-0091).
 *
 * Same visual family as `/login`: standalone centered card on a soft
 * background, Schoology wordmark above (no `<LearnShell>` — pre-auth).
 * Wires name + email + password to `trpc.learnerAuth.register`, which
 * provisions the tenant-bound learner AND signs them in (sets the
 * session cookie) — so on success we invalidate `auth.getCurrentUser`
 * and land straight on `/catalog`.
 *
 * Anti-bot honeypot: a visually-hidden `website` input that real users
 * never see or tab into. Its (empty) value rides along as `honeypot` —
 * the server rejects any submission where it's filled.
 *
 * CONFLICT (email already registered) gets a friendly inline message
 * with a link to `/login` instead of the raw error string.
 */
import * as React from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowRight, Eye, EyeOff, Leaf } from 'lucide-react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from '@mangrove/ui';
import { useQueryClient } from '@tanstack/react-query';
import { trpc } from '../lib/trpc.js';
import { useCurrentUser, wipeClientCaches } from '../lib/auth.js';

export const Route = createFileRoute('/signup')({
  component: SignupPage,
});

/** Narrow an unknown tRPC error to "is this a 409 CONFLICT?" (settings/index.tsx idiom). */
function isConflictError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  const data = (err as { data?: { code?: unknown } }).data;
  return data?.code === 'CONFLICT';
}

function SignupPage(): React.JSX.Element {
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const queryClient = useQueryClient();
  const me = useCurrentUser();

  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  // Honeypot — stays empty for humans; bots auto-filling forms trip it.
  const [website, setWebsite] = React.useState('');
  const [formError, setFormError] = React.useState<string | null>(null);

  const register = trpc.learnerAuth.register.useMutation({
    onSuccess: async () => {
      // §3.3 shared-device wipe, run UNCONDITIONALLY on every successful
      // signup (same choice as /login: simpler + safer than diffing the
      // previously-cached user). AWAITED before navigate so no stale
      // bucket survives into the next screen.
      await wipeClientCaches(queryClient);
      await utils.auth.getCurrentUser.invalidate();
      void navigate({ to: '/catalog', replace: true });
    },
  });

  // Already signed in → straight to the catalog.
  React.useEffect(() => {
    if (!me.isPending && me.data) {
      void navigate({ to: '/catalog', replace: true });
    }
  }, [me.isPending, me.data, navigate]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    if (register.isPending) return;
    if (!name.trim()) {
      setFormError('Enter your name.');
      return;
    }
    if (!email.trim()) {
      setFormError('Enter your email.');
      return;
    }
    if (password.length < 8) {
      setFormError('Password must be at least 8 characters.');
      return;
    }
    setFormError(null);
    register.mutate({
      name: name.trim(),
      email: email.trim(),
      password,
      honeypot: website,
    });
  };

  const conflict = isConflictError(register.error);
  const errorMessage = formError ?? (!conflict && register.error ? register.error.message : null);

  return (
    <div className="page-canvas flex min-h-dvh flex-col items-center justify-center px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <SchoologyWordmark />
        <Card className="card-soft animate-fade-in-up w-full">
          <CardHeader className="space-y-3">
            <Badge
              variant="secondary"
              className="w-fit gap-1.5 rounded-full bg-primary/10 text-xs font-semibold tracking-wide text-primary"
            >
              <span
                className="size-1.5 rounded-full bg-primary ring-4 ring-primary/20"
                aria-hidden="true"
              />
              SAT · GRE · Medical Admission
            </Badge>
            <div className="space-y-1">
              <CardTitle className="text-2xl tracking-tight">Create your account</CardTitle>
              <CardDescription>Practice exams, real scores — free.</CardDescription>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} noValidate className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="signup-name">Name</Label>
                <Input
                  id="signup-name"
                  data-testid="signup-name"
                  type="text"
                  autoComplete="name"
                  placeholder="Your full name"
                  className="min-h-touch transition-shadow focus-visible:ring-2 focus-visible:ring-primary/40"
                  disabled={register.isPending}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signup-email">Email</Label>
                <Input
                  id="signup-email"
                  data-testid="signup-email"
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  className="min-h-touch transition-shadow focus-visible:ring-2 focus-visible:ring-primary/40"
                  disabled={register.isPending}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="signup-password">Password</Label>
                <div className="relative">
                  <Input
                    id="signup-password"
                    data-testid="signup-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    placeholder="Create a password"
                    className="min-h-touch pr-11 transition-shadow focus-visible:ring-2 focus-visible:ring-primary/40"
                    disabled={register.isPending}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    aria-describedby="signup-password-hint"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="absolute right-1 top-1/2 grid size-9 -translate-y-1/2 place-items-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                  >
                    {showPassword ? (
                      <EyeOff className="size-4" aria-hidden="true" />
                    ) : (
                      <Eye className="size-4" aria-hidden="true" />
                    )}
                  </button>
                </div>
                <p id="signup-password-hint" className="text-xs text-muted-foreground">
                  At least 8 characters.
                </p>
              </div>
              {/* Honeypot — hidden from humans (visually + from the tab
                  order); bots that fill every field get rejected. */}
              <div aria-hidden="true" className="sr-only">
                <label htmlFor="signup-website">Website</label>
                <input
                  id="signup-website"
                  name="website"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                />
              </div>
              {conflict ? (
                <p
                  role="alert"
                  data-testid="signup-error"
                  className="text-sm font-medium text-destructive"
                >
                  That email is already registered —{' '}
                  <Link to="/login" className="font-medium underline underline-offset-4">
                    log in instead
                  </Link>
                  .
                </p>
              ) : errorMessage ? (
                <p
                  role="alert"
                  data-testid="signup-error"
                  className="text-sm font-medium text-destructive"
                >
                  {errorMessage}
                </p>
              ) : null}
              <Button
                type="submit"
                data-testid="signup-submit"
                disabled={register.isPending}
                className="press group min-h-touch w-full"
              >
                {register.isPending ? 'Creating your account…' : 'Create account'}
                {!register.isPending ? (
                  <ArrowRight
                    className="size-4 transition-transform group-hover:translate-x-0.5"
                    aria-hidden="true"
                  />
                ) : null}
              </Button>
            </form>
          </CardContent>
        </Card>
        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{' '}
          <Link
            to="/login"
            className="font-semibold text-primary underline-offset-4 hover:underline"
          >
            Sign in
          </Link>
        </p>
        <p className="text-center text-xs text-muted-foreground">
          By creating an account you agree to our Terms and Privacy Policy.
        </p>
      </div>
    </div>
  );
}

/** Brand lockup above the card — same glyph family as `<LearnShell>`. */
function SchoologyWordmark(): React.JSX.Element {
  return (
    <div className="animate-fade-in flex items-center justify-center gap-2.5 text-2xl font-semibold tracking-tight">
      <span className="flex size-9 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-brand-blue shadow-sm">
        <Leaf className="size-5 text-primary-foreground" aria-hidden="true" />
      </span>
      <span className="bg-gradient-to-r from-primary to-brand-blue bg-clip-text text-transparent">
        Schoology
      </span>
    </div>
  );
}
